import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useMemo } from 'react';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { OutpatientClinicTaskListFilter } from '../types/outpatientClinicTaskListFilter';
import { emptyOutpatientClinicTaskListFilter } from '../constants/emptyOutpatientClinicTaskListFilter';
import { fetchOutpatientClinicTasks } from '../services/fetchOutpatientClinicTasks';

export type UseFilteredOutpatientClinicTasksProps = {
    filter: OutpatientClinicTaskListFilter;
    pagination: PaginationState;
    sorting: SortingState;
};

export function useFilteredOutpatientClinicTasks({
    filter,
    pagination,
    sorting,
}: UseFilteredOutpatientClinicTasksProps) {
    const abortSignal = useAbortSignal();
    const isEmptyFilter = useMemo(() => {
        return isEqual(filter, emptyOutpatientClinicTaskListFilter);
    }, [filter]);

    const { data, isLoading, error } = useQuery({
        queryFn: () => fetchOutpatientClinicTasks({ abortSignal, filter, pagination, sorting }),
        queryKey: ['outpatient-clinic-tasks-filtered', filter, pagination, sorting],
    });

    return {
        error,
        filteredTasks: data?.items,
        isEmptyFilter,
        isLoading,
        total: data?.total ?? 0,
    };
}
